/**
 * Tiny stale-while-revalidate helper backed by `localStorage`, with an
 * IndexedDB tier for payloads too large for it (see `MAX_ENTRY_CHARS`).
 *
 * Pattern:
 *  1. Synchronously read a recent cached payload (≤ `MAX_AGE_MS` old) and hand
 *     it to the caller via `onData(cached, true)` so the UI renders instantly.
 *     Large payloads live in IndexedDB and arrive a few ms later instead —
 *     `hasFreshSwrCache()` still answers "is it cached?" synchronously so the
 *     page doesn't flash a spinner in the meantime.
 *  2. Kick off `fetcher()` in the background, persist the fresh result, and
 *     hand it to the caller via `onData(fresh, false)`.
 *  3. If the network call fails AND no cached value was rendered, surface the
 *     error to `onError`. If cached was already rendered we silently keep the
 *     stale view rather than flashing an error over already-good content.
 *
 * Used to make the public site feel instant on revisit even when the Function
 * App is cold-starting on the first hit.
 *
 * NOTE: This is NOT a substitute for the server-side response cache or the
 * `Cache-Control` headers — it complements them. The browser HTTP cache may
 * also serve fresh responses without a network round-trip; this helper only
 * kicks in when the page-level state was lost (full reload, new tab, etc.).
 */

import { idbDelete, idbDeleteByPrefix, idbEvictOlderThan, idbGet, idbSet } from './idbCache';

/** How long a localStorage entry is considered "fresh enough" to hydrate from.
 *  Generous because the user immediately sees a refresh from the live fetch. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

const KEY_PREFIX = 'mhe:swr:v1:';

/**
 * Largest single entry we will persist **to localStorage**, in UTF-16 code units.
 *
 * `localStorage` is ~5 MB per origin and stores UTF-16, so a 1M-character
 * payload can occupy ~2 MB on its own — roughly 40% of the whole budget. Any
 * entry bigger than this is diverted to IndexedDB rather than written here,
 * because the only way to make room for it is to evict everything else, which
 * defeats the purpose of the cache.
 *
 * The `/api/events/published` payload is still over this limit even after the
 * Phase 2 field allowlist (~2.3M chars). It used to be dropped entirely — the
 * write threw `QuotaExceededError` every time — so the events list, the single
 * biggest and slowest request on the site, was the one thing the 24h cache
 * never covered. It now goes to IndexedDB instead (Phase 9).
 */
const MAX_ENTRY_CHARS = 1024 * 1024;

/**
 * Sync existence index for entries that live in IndexedDB.
 *
 * IndexedDB is async, but `hasFreshSwrCache()` has to answer "do we have this?"
 * synchronously during render so a page can decide whether to show a spinner or
 * wait a tick for cached data. Storing just `key -> timestamp` in localStorage
 * keeps that question answerable without blocking: a few dozen bytes per entry
 * rather than the megabytes the payload itself would cost.
 */
const IDB_INDEX_PREFIX = 'mhe:swr:idb:v1:';

/** Upper bound on evictions per write, so a pathological payload can't spin
 *  through the entire namespace one `removeItem` at a time. */
const MAX_EVICTIONS = 32;

interface Entry<T> {
  ts: number;
  data: T;
}

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    // Browsers in private mode / cookies-blocked may throw on access.
    return null;
  }
}

/** Storage quota errors are reported inconsistently across browsers. */
function isQuotaError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const err = e as { name?: string; code?: number };
  return err.name === 'QuotaExceededError'
    || err.name === 'NS_ERROR_DOM_QUOTA_REACHED' // Firefox
    || err.code === 22 // Chrome/Safari DOMException code
    || err.code === 1014; // Firefox legacy code
}

/**
 * Pull `ts` off a serialized entry without a full `JSON.parse`.
 *
 * We always write `{"ts":<number>,"data":…}`, so a prefix match is enough — and
 * parsing a multi-megabyte entry just to read its timestamp would make eviction
 * more expensive than the write it is trying to enable. Anything unrecognised
 * (corrupt, or written by an older version) sorts to 0 so it is evicted first.
 */
function entryTimestamp(raw: string | null): number {
  if (!raw) return 0;
  const m = /^\{"ts":(\d+)/.exec(raw);
  return m ? Number(m[1]) : 0;
}

/**
 * Our own cache keys, oldest first.
 *
 * Deliberately filtered to `KEY_PREFIX`: MSAL keeps its account and token cache
 * in this same `localStorage` (see the auth invariants in
 * `.github/copilot-instructions.md`), and evicting those would sign the user out
 * — the exact failure this cache is supposed to help avoid.
 */
function ourKeysOldestFirst(storage: Storage): string[] {
  const entries: { key: string; ts: number }[] = [];
  for (let i = storage.length - 1; i >= 0; i--) {
    const k = storage.key(i);
    if (!k || !k.startsWith(KEY_PREFIX)) continue;
    entries.push({ key: k, ts: entryTimestamp(storage.getItem(k)) });
  }
  entries.sort((a, b) => a.ts - b.ts);
  return entries.map(e => e.key);
}

export function readSwrCache<T>(key: string): T | null {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(KEY_PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as Entry<T>;
    if (!entry || typeof entry.ts !== 'number') return null;
    if (Date.now() - entry.ts > MAX_AGE_MS) return null;
    return entry.data;
  } catch {
    return null;
  }
}

/** Timestamp of this key's IndexedDB entry, from the sync localStorage index. */
function idbIndexTs(key: string): number {
  const storage = safeStorage();
  if (!storage) return 0;
  try {
    const raw = storage.getItem(IDB_INDEX_PREFIX + key);
    const ts = raw ? Number(raw) : 0;
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
}

function setIdbIndex(key: string, ts: number): void {
  const storage = safeStorage();
  if (!storage) return;
  try { storage.setItem(IDB_INDEX_PREFIX + key, String(ts)); } catch { /* index is best-effort */ }
}

function clearIdbIndex(key: string): void {
  const storage = safeStorage();
  if (!storage) return;
  try { storage.removeItem(IDB_INDEX_PREFIX + key); } catch { /* ignore */ }
}

let evictionScheduled = false;

/**
 * Sweep expired IndexedDB entries once per session.
 *
 * Cache keys embed the date window and filter state, so browsing over weeks
 * mints new keys continuously. Without a sweep, every abandoned key keeps a
 * multi-megabyte payload alive until the browser evicts the whole origin —
 * which it does by dropping *everything*, including the entry we actually want.
 * Deliberately idle-scheduled: this is housekeeping and must never compete with
 * first paint.
 */
function scheduleIdbEviction(): void {
  if (evictionScheduled) return;
  evictionScheduled = true;
  const run = () => {
    void idbEvictOlderThan(MAX_AGE_MS);
    // Drop matching index entries too, or the sync index would keep claiming a
    // cache exists for payloads that have just been deleted.
    const storage = safeStorage();
    if (!storage) return;
    try {
      const cutoff = Date.now() - MAX_AGE_MS;
      for (let i = storage.length - 1; i >= 0; i--) {
        const k = storage.key(i);
        if (!k || !k.startsWith(IDB_INDEX_PREFIX)) continue;
        const ts = Number(storage.getItem(k));
        if (!Number.isFinite(ts) || ts < cutoff) storage.removeItem(k);
      }
    } catch { /* best-effort */ }
  };
  try {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 5000 });
    else setTimeout(run, 3000);
  } catch {
    setTimeout(run, 3000);
  }
}

/**
 * Synchronously report whether a fresh cached payload exists, in EITHER store.
 *
 * Callers use this to decide whether to blank the view and show a spinner. It
 * exists because `readSwrCache` can only return data that lives in
 * localStorage; a large payload lives in IndexedDB and can't be read
 * synchronously, but "is it there?" is answerable from the sync index — which
 * is all the spinner decision actually needs.
 */
export function hasFreshSwrCache(key: string): boolean {
  if (readSwrCache(key) !== null) return true;
  const ts = idbIndexTs(key);
  return ts > 0 && Date.now() - ts <= MAX_AGE_MS;
}

/** Async read that checks localStorage first, then IndexedDB. */
async function readSwrCacheAsync<T>(key: string): Promise<T | null> {
  const sync = readSwrCache<T>(key);
  if (sync !== null) return sync;
  const ts = idbIndexTs(key);
  if (!ts || Date.now() - ts > MAX_AGE_MS) return null;
  const entry = await idbGet<T>(key);
  if (!entry || typeof entry.ts !== 'number') return null;
  if (Date.now() - entry.ts > MAX_AGE_MS) return null;
  return entry.data;
}

/**
 * Invalidate one specific cache entry. Call this immediately after a mutation
 * (PATCH/POST/DELETE) so the next page that reads it via `swrLoad` won't be
 * served the stale snapshot. The next `swrLoad` will fall through straight to
 * the fetcher.
 */
export function clearSwrCache(key: string): void {
  const storage = safeStorage();
  clearIdbIndex(key);
  void idbDelete(key);
  if (!storage) return;
  try { storage.removeItem(KEY_PREFIX + key); } catch { /* ignore */ }
}

/**
 * Invalidate every cache entry whose key starts with `prefix`. Useful for
 * keys that include query params (e.g. `events:<from>|<to>`) where we want
 * to nuke every variant after a mutation.
 */
export function clearSwrCacheByPrefix(prefix: string): void {
  const storage = safeStorage();
  void idbDeleteByPrefix(prefix);
  if (!storage) return;
  const fullPrefix = KEY_PREFIX + prefix;
  const idbIndexFullPrefix = IDB_INDEX_PREFIX + prefix;
  try {
    for (let i = storage.length - 1; i >= 0; i--) {
      const k = storage.key(i);
      if (k && (k.startsWith(fullPrefix) || k.startsWith(idbIndexFullPrefix))) storage.removeItem(k);
    }
  } catch { /* ignore */ }
}

/**
 * Persist one entry, best-effort.
 *
 * Previously this caught `QuotaExceededError` and removed **every** key under
 * `KEY_PREFIX`, so a single oversized write (the events list, which always
 * overflowed) silently wiped the venue, series, map and report caches too — the
 * advertised 24h cache had effectively never worked. Now:
 *
 *  - entries over `MAX_ENTRY_CHARS` are skipped before we ever touch storage;
 *  - on a genuine quota error we evict our own oldest entries one at a time and
 *    retry, so the newest write wins and unrelated caches survive.
 */
export function writeSwrCache<T>(key: string, data: T): void {
  const storage = safeStorage();
  if (!storage) return;
  const fullKey = KEY_PREFIX + key;

  let serialized: string;
  try {
    serialized = JSON.stringify({ ts: Date.now(), data });
  } catch {
    return; // circular structure, BigInt, etc. — nothing to cache.
  }

  if (serialized.length > MAX_ENTRY_CHARS) {
    // Too big for localStorage — divert to IndexedDB and drop any stale copy
    // here so the two stores can't disagree about this key.
    try { storage.removeItem(fullKey); } catch { /* ignore */ }
    const ts = Date.now();
    // Index first: a write that succeeds but whose index write is lost would be
    // invisible forever, whereas an index entry whose IDB write fails just
    // means one wasted async read that returns null.
    setIdbIndex(key, ts);
    void idbSet(key, ts, data).catch(() => clearIdbIndex(key));
    scheduleIdbEviction();
    return;
  }

  // Small enough for localStorage. Clear any prior IndexedDB copy so a shrunken
  // payload doesn't leave a larger stale one behind to be read later.
  if (idbIndexTs(key) > 0) {
    clearIdbIndex(key);
    void idbDelete(key);
  }

  try {
    storage.setItem(fullKey, serialized);
    return;
  } catch (e) {
    if (!isQuotaError(e)) return; // e.g. storage disabled mid-session
  }

  let candidates: string[];
  try {
    candidates = ourKeysOldestFirst(storage).filter(k => k !== fullKey);
  } catch {
    return;
  }

  for (const k of candidates.slice(0, MAX_EVICTIONS)) {
    try {
      storage.removeItem(k);
      storage.setItem(fullKey, serialized);
      return;
    } catch (e) {
      if (!isQuotaError(e)) return;
      // else: still too tight — evict the next-oldest and try again.
    }
  }
  // Out of room even after eviction. Give up silently; the cache is best-effort
  // and the caller always follows up with a live fetch.
}

interface SwrLoadOptions<T> {
  /** Stable cache key (include any query params that vary the response). */
  key: string;
  /** Network fetcher. Should throw on error. */
  fetcher: () => Promise<T>;
  /** Receives both the cached payload (instantly, fromCache=true) and the
   *  fresh payload (fromCache=false). Safe to call setState from here. */
  onData: (data: T, fromCache: boolean) => void;
  /** Called only if the network fetch fails AND no cache was served. */
  onError?: (err: unknown) => void;
  /** Optional cancellation flag — when true, onData/onError won't be invoked
   *  for the network result (cached delivery has already happened sync). */
  isCancelled?: () => boolean;
}

/**
 * Stale-while-revalidate loader. Synchronously delivers any cached payload
 * via `onData`, then fetches fresh data and delivers that too. Returns the
 * fetch promise so callers can await network completion if needed (most
 * pages just fire-and-forget).
 */
export function swrLoad<T>(opts: SwrLoadOptions<T>): Promise<void> {
  const { key, fetcher, onData, onError, isCancelled } = opts;
  const cached = readSwrCache<T>(key);
  let deliveredCache = cached !== null;
  let deliveredFresh = false;
  if (deliveredCache && !(isCancelled?.() ?? false)) {
    onData(cached as T, true);
  }

  // Nothing in localStorage, but the sync index says a large payload is in
  // IndexedDB — read it asynchronously and deliver if it beats the network.
  const idbPromise: Promise<T | null> = !deliveredCache && hasFreshSwrCache(key)
    ? readSwrCacheAsync<T>(key).catch(() => null)
    : Promise.resolve(null);

  void idbPromise.then((value) => {
    if (value === null) return;
    // Out-of-order guard: the network can win this race, and delivering a
    // cached payload after a fresh one would visibly revert the page to stale
    // data. Fresh always wins, regardless of arrival order.
    if (deliveredFresh || (isCancelled?.() ?? false)) return;
    deliveredCache = true;
    onData(value, true);
  });

  return (async () => {
    try {
      const fresh = await fetcher();
      deliveredFresh = true;
      if (isCancelled?.()) return;
      writeSwrCache(key, fresh);
      onData(fresh, false);
    } catch (e) {
      if (isCancelled?.()) return;
      // Only surface the error if nothing was (or will be) rendered from cache;
      // await the IDB read rather than racing it, or a slow cache hit would
      // flash an error over content that is about to appear.
      const hadCache = deliveredCache || (await idbPromise) !== null;
      if (!hadCache && onError) onError(e);
      // If we had cache, keep the cached view rather than surfacing an error.
    }
  })();
}
